import { useEffect } from 'react';
export var DND_AUTO_SCROLL_EDGE_PX = 120;
var MIN_SCROLL_SPEED_PX_PER_SECOND = 180;
var MAX_SCROLL_SPEED_PX_PER_SECOND = 1400;
var MAX_FRAME_DELTA_SECONDS = 0.05;
var HORIZONTAL_TOLERANCE_PX = 24;
export var isPointerInScrollEdge = function isPointerInScrollEdge(container, clientY) {
  var edgePx = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : DND_AUTO_SCROLL_EDGE_PX;

  if (!container) {
    return false;
  }

  var rect = container.getBoundingClientRect();

  if (clientY < rect.top || clientY > rect.bottom) {
    return false;
  }

  return clientY - rect.top < edgePx || rect.bottom - clientY < edgePx;
};
/**
 * HTML5 DnD does not auto-scroll nested overflow containers.
 * Listen to native `dragover` (no React re-renders) and scroll when the pointer
 * is near the top/bottom edge. Live hover-reorder is paused in that zone so
 * layout shifts do not fight the scroll.
 */

export var useDragAutoScroll = function useDragAutoScroll(containerRef) {
  useEffect(function () {
    var pointer = {
      x: 0,
      y: 0,
      active: false
    };
    var frameId = 0;
    var running = false;
    var previousTimestamp = 0;
    var containerRect = null;

    var stop = function stop() {
      pointer.active = false;
      running = false;
      previousTimestamp = 0;
      containerRect = null;

      if (frameId) {
        window.cancelAnimationFrame(frameId);
        frameId = 0;
      }
    };

    var getScrollDirectionAndStrength = function getScrollDirectionAndStrength() {
      if (!containerRect) return null;
      var distanceFromTop = pointer.y - containerRect.top;
      var distanceFromBottom = containerRect.bottom - pointer.y;
      var inHorizontalBounds = pointer.x >= containerRect.left - HORIZONTAL_TOLERANCE_PX && pointer.x <= containerRect.right + HORIZONTAL_TOLERANCE_PX;
      if (!inHorizontalBounds) return null;

      if (distanceFromTop >= 0 && distanceFromTop < DND_AUTO_SCROLL_EDGE_PX) {
        return {
          direction: -1,
          strength: (DND_AUTO_SCROLL_EDGE_PX - distanceFromTop) / DND_AUTO_SCROLL_EDGE_PX
        };
      }

      if (distanceFromBottom >= 0 && distanceFromBottom < DND_AUTO_SCROLL_EDGE_PX) {
        return {
          direction: 1,
          strength: (DND_AUTO_SCROLL_EDGE_PX - distanceFromBottom) / DND_AUTO_SCROLL_EDGE_PX
        };
      }

      return null;
    };

    var tick = function tick(timestamp) {
      if (!pointer.active) {
        running = false;
        return;
      }

      var container = containerRef.current;
      var edge = getScrollDirectionAndStrength();

      if (!container || !edge) {
        running = false;
        previousTimestamp = 0;
        frameId = 0;
        return;
      }

      if (!previousTimestamp) {
        previousTimestamp = timestamp;
        frameId = window.requestAnimationFrame(tick);
        return;
      }

      var elapsedSeconds = Math.min((timestamp - previousTimestamp) / 1000, MAX_FRAME_DELTA_SECONDS);
      previousTimestamp = timestamp;
      var speed = MIN_SCROLL_SPEED_PX_PER_SECOND + edge.strength * (MAX_SCROLL_SPEED_PX_PER_SECOND - MIN_SCROLL_SPEED_PX_PER_SECOND);
      var maxScroll = container.scrollHeight - container.clientHeight;
      var next = Math.min(Math.max(0, container.scrollTop + edge.direction * speed * elapsedSeconds), maxScroll);

      if (next === container.scrollTop) {
        running = false;
        previousTimestamp = 0;
        frameId = 0;
        return;
      }

      container.scrollTop = next;
      frameId = window.requestAnimationFrame(tick);
    };

    var onDragOver = function onDragOver(event) {
      pointer.x = event.clientX;
      pointer.y = event.clientY;
      pointer.active = true;

      if (!containerRect) {
        var _containerRef$current, _containerRef$current2;

        containerRect = (_containerRef$current = (_containerRef$current2 = containerRef.current) === null || _containerRef$current2 === void 0 ? void 0 : _containerRef$current2.getBoundingClientRect()) !== null && _containerRef$current !== void 0 ? _containerRef$current : null;
      }

      if (!getScrollDirectionAndStrength()) {
        return;
      }

      if (!running) {
        running = true;
        frameId = window.requestAnimationFrame(tick);
      }
    };

    var onResize = function onResize() {
      containerRect = null;
    };

    document.addEventListener('dragover', onDragOver);
    document.addEventListener('dragend', stop);
    document.addEventListener('drop', stop);
    window.addEventListener('resize', onResize);
    return function () {
      stop();
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('dragend', stop);
      document.removeEventListener('drop', stop);
      window.removeEventListener('resize', onResize);
    };
  }, [containerRef]);
};